import Handsontable from 'handsontable';
import { RecordInfo } from '../../../reviewEntries/type';
import { Validator } from '../../../reviewEntries/validator';
import FilterStrategy from '../columns/FilterStrategy';
import { BehaviorSubject } from 'rxjs';

export enum ModeViewTableState {
  ALL,
  ERROR,
}

class ModeViewTable {
  private validator?: Validator;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dataSet: Record<string, any>[] = [];
  private dataInfos: Record<string, RecordInfo[]> = {};
  private hotInstance?: Handsontable;
  private hiddenRows: number[] = [];
  private filtering: FilterStrategy;
  private viewState = ModeViewTableState.ALL;
  private readOnly: boolean;
  private _modeViewTableObservable: BehaviorSubject<ModeViewTable>;

  constructor(filtering: FilterStrategy, readOnly: boolean) {
    this.filtering = filtering;
    this.readOnly = readOnly;
    this._modeViewTableObservable = new BehaviorSubject<ModeViewTable>(this);
  }

  modeViewTableObservable = () => {
    return this._modeViewTableObservable;
  };

  private calculateHideRows = () => {
    const hotInstance = this.hotInstance;
    if (!hotInstance || !this.validator) {
      return [];
    }
    const validRows = this.validator.getCorrectRows(
      this.dataSet.length - 1,
      this.dataInfos,
      hotInstance
    );

    if (!this.readOnly) {
      const lastRow = hotInstance.toVisualRow(this.dataSet.length - 1);
      if (lastRow !== null) {
        validRows.push(lastRow);
      }
    }

    const hiddenByFilterRowIndexes =
      this.filtering.getFilteredHideRowIndexMaps();

    const hiddenRows = validRows.filter((rowIndex) => {
      if (hiddenByFilterRowIndexes[rowIndex]) {
        return false;
      } else {
        return true;
      }
    });

    return hiddenRows;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDataSet = (dataSet: Record<string, any>[]) => {
    this.dataSet = dataSet;
  };

  setValidator = (validator: Validator) => {
    this.validator = validator;
  };

  setDataInfos = (dataInfos: Record<string, RecordInfo[]>) => {
    this.dataInfos = dataInfos;
  };

  setHotInstance = (hotInstance: Handsontable) => {
    this.hotInstance = hotInstance;
  };

  recalculate = () => {
    if (this.viewState === ModeViewTableState.ERROR && this.hotInstance) {
      const hiddenRows = this.calculateHideRows();
      this.hiddenRows = hiddenRows;
      this.hotInstance.updateSettings({
        hiddenRows: {
          rows: hiddenRows,
        },
      });
    }
  };

  hideRows = (isEmit = false) => {
    if (!this.hotInstance || !this.validator) {
      return;
    }

    const hiddenPlugin = this.hotInstance.getPlugin('hiddenRows');

    if (!hiddenPlugin) {
      return;
    }

    const hiddenRows = this.calculateHideRows();

    hiddenPlugin.hideRows(hiddenRows);
    this.hiddenRows = hiddenRows;
    this.viewState = ModeViewTableState.ERROR;
    if (isEmit) {
      this._modeViewTableObservable.next(this);
    }
  };

  showRows = (isEmit = false) => {
    if (!this.hotInstance) {
      return;
    }

    const hiddenPlugin = this.hotInstance.getPlugin('hiddenRows');

    if (!hiddenPlugin) {
      return;
    }

    hiddenPlugin.showRows(this.hiddenRows);
    this.hiddenRows = [];
    this.viewState = ModeViewTableState.ALL;
    if (isEmit) {
      this._modeViewTableObservable.next(this);
    }
  };

  getHiddenRows = () => {
    return this.hiddenRows;
  };

  getViewState = () => {
    return this.viewState;
  };
}

export default ModeViewTable;
